import ProgressBar from "../../../components/lib/Progress/ProgressBar";
import { useState, useEffect, useReducer } from "react";
import { Heading } from "../../../components/lib/Text/Heading";
import PageWrapper from "../../../components/lib/Wrapper/PageWrapper";
import Slider from "../../../components/lib/Slider/Slider";
import SliderContent from "../../../components/lib/Slider/SliderContent";
import { encryptSubmitAnswers } from "../../../lib/encrypt";
import useFormAnalytics from "../../../hooks/useFormAnalytics";
import { FormData, Page } from "../../../lib/ssi/form.types";
import { submitForm } from "../../../lib/api/forms/[formId]/submit";
import { addToScore, createScore, Score } from "../../../lib/types/form/submission/score";
import { FormShortId, PageId } from "../../../lib/types/ids";
import { getServerPublicApiKey } from "../../../lib/env";
import { BadRequestBodyError } from "../../../lib/bad-request-body-error";
import { UnexpectedValueError } from "../../../lib/unexpected-value-error";
import { UnencryptedAnswers } from "../../../lib/types/form/submission/answer";
import { getFormData } from "../../../lib/ssi/form";
import { resolveExpr } from "../../../lib/integrations/mapKeyToValue";
import { resolveStaticPath } from "../../../lib/types/static-path";

// Hier werden die Formulare angezeigt

interface FormPageProps {
  formData: string;
  initialActivePage?: ActivePageId,
  saveCurrentPage?: (pageId: PageId) => void;
  isPreview?: boolean;
  registerSetAnswers?: (value?: { setter: ((answers: UnencryptedAnswers) => void) } | undefined) => void,
  initialAnswers?: UnencryptedAnswers,
  saveAnswers?: (answers: UnencryptedAnswers) => void;
}

const parseFormDataString = (formData: string): FormData | null => (
  !formData ? null
    : BadRequestBodyError.throwIfLeft(
      FormData.decode(JSON.parse(formData)))
);

type ActivePageId = PageId | "thank-you" | undefined;

function findIndexForPageId(form: FormData | null, activePageId: ActivePageId) {
  const result = form?.sortedPages.findIndex(
    (id) => id === activePageId
  ) ?? 0;
  if (result === -1) return 0;
  return result;
}

function coerceActivePageId(items: PageId[] | undefined, pageId: ActivePageId): ActivePageId {
  if (!items)
    return undefined;

  if (!pageId)
    return items[0];

  if (pageId === "thank-you")
    return "thank-you";

  const index = items.indexOf(pageId);
  if (index === -1)
    return items[0];

  return items[index];
}

const FormPage: React.FunctionComponent<FormPageProps> = ({
  formData, isPreview, initialActivePage, saveCurrentPage,
  initialAnswers, registerSetAnswers, saveAnswers
}) => {
  const form = parseFormDataString(formData);
  const { trackFormEvent } = useFormAnalytics(!!isPreview);
  const [activePage, setActivePage] = useState<ActivePageId>(coerceActivePageId(form?.sortedPages, initialActivePage));
  const [activeIndex, setActiveIndex] = useState(findIndexForPageId(form, initialActivePage));
  const [isLoading, setIsLoading] = useState(false);
  const [isStarted, setIsStarted] = useState(false);
  const [wasViewed, setWasViewed] = useState(false);
  const [answers, setAnswers] = useReducer(
    (state: UnencryptedAnswers, newState: UnencryptedAnswers) => {
      const updated = ({ ...state, ...newState });
      //console.log("answers", updated);
      return updated;
    },
    initialAnswers ?? {}
  );
  const initialScore: { [key: string]: Score } = {};
  const [score, setScore] = useState(initialScore);

  useEffect(function mount() {
    if (!registerSetAnswers) return;

    registerSetAnswers({ setter: answers => setAnswers(answers) });

    return function unMount() {
      registerSetAnswers();
    };
  }, [registerSetAnswers]);

  // Wird aufgerufen, wenn sich die aktive Page verändert
  useEffect(() => {
    if (form?.id) {
      // Index der aktuellen Seite im Formular
      setActiveIndex(findIndexForPageId(form, activePage));
    }
  }, [activePage]);

  useEffect(() => {
    if (saveAnswers) {
      saveAnswers(answers);
    }
  }, [saveAnswers, answers]);

  useEffect(() => {
    if (saveCurrentPage && activePage && activePage !== "thank-you") {
      saveCurrentPage(activePage);
    }
  }, [saveCurrentPage, activePage]);

  useEffect(() => {
    if (!form) return;

    // Tracking des Formulars
    if (!isStarted && activeIndex > 0) {
      setIsStarted(true);
      trackFormEvent({ id: form.id, event: "start" });
    }

    // Einstellen der iFrame-Höhe in externen Webseiten
    if (window.parent && window.parent != window) {
      setTimeout(() => {
        const body = document.body;
        const html = document.documentElement;
        const height = Math.max(
          body.scrollHeight,
          body.offsetHeight,
          html.clientHeight,
          html.scrollHeight,
          html.offsetHeight
        );
        const formId = `${form.handle}-${form.shortId}`;
        window.parent.postMessage("setHeight:" + height + ":" + formId, "*");
      }, 400);
    }
  }, [activeIndex]);

  // Wird nur beim ersten Rendern aufgerufen
  useEffect(() => {
    // Tracking
    if (form?.id) {
      if (!wasViewed) {
        trackFormEvent({ id: form.id, event: "view" });
        setWasViewed(true);
      }
      const pageId = coerceActivePageId(form.sortedPages, initialActivePage);
      setActivePage(pageId);
      setActiveIndex(findIndexForPageId(form, pageId));
    }
  }, []);

  if (!formData) return null;

  // Anfrage wird verschlüsst & abgeschickt
  const setAnswersAndPublish = async (data: UnencryptedAnswers) => {
    if (!form) return;

    if (!isLoading) {
      const allAnswers = { ...answers, ...data };
      try {
        setIsLoading(true);
        const publicKey = await getServerPublicApiKey();
        const allAnswersWithEncryption = await encryptSubmitAnswers(publicKey, allAnswers);
        let totalScore = createScore(0);
        for (const id in score) {
          const item = score[id] as typeof score[typeof id];
          totalScore = addToScore(totalScore, item);
        }
        if (!isPreview) {
          await submitForm({
            id: form.id,
            answers: allAnswersWithEncryption,
            score: totalScore,
          });
        }
        trackFormEvent({ id: form.id, event: "submit" });
        setActivePage("thank-you");
      } catch (err) {
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const resolveExpression = (expression: string) => {
    const args = {
      expression,
      submission: {
        createdAt: new Date(),
        score: createScore(0),
        userLogo: null,
        formProperties: form?.properties ?? {},
      },
      answers: answers,
      variables: {},
    };
    //console.log("resolveExpression", JSON.stringify(args));
    return resolveExpr(args);
  };

  if (activePage === "thank-you") {
    // Anzeige, nachdem das Formular abgeschickt wurde
    // Text kann in der Datenbank angepasst werden
    return (
      <div className="theme-bfit flex flex-col items-center py-40">
        <div className="max-w-xl text-center">
          <img
            src={resolveStaticPath(form?.thankYouPage?.icon) ?? "/static/checkmark-large.jpg"}
            width="60px"
            className="mx-auto mb-4"
          />
          <Heading level={2} className="text-center mb-2">
            {form?.thankYouPage?.heading ??
              "Deine Anfrage wurde erfolgreich verschickt. Schaue in dein Email-Postfach, um den Vertrag zu aktivieren."}
          </Heading>
          {form?.thankYouPage?.description && (
            <p className="max-w-lg mx-auto">{form?.thankYouPage?.description}</p>
          )}
          {isPreview && (
            <p className="max-w-lg mx-auto">Hinweis: Der Text oben ist nur eine Vorschau und die Anfrage wurde nicht an den Server geschickt.</p>
          )}
        </div>
      </div>
    );
  }

  const currentPage: Page | null = form?.pages.find(
    (el: Page) => el.id === activePage
  ) ?? null;
  const wrappers = form?.sortedPages?.map((id: string, index: number) => {
    const nextPage =
      index !== form.pages.length - 1 ? form.sortedPages[index + 1] : undefined;

    const previousPage = index != 0 ? form.sortedPages[index - 1] : undefined;

    const data = form.pages.find((el: Page) => el.id === id);

    if (data?.id && activePage)
      // Jede einzelne Seite des Formulars wird in ein Pagewrapper eingefügt
      return (
        <PageWrapper
          key={data.id}
          data={data}
          index={index}
          activePage={activePage}
          activeIndex={activeIndex}
          resolveExpression={resolveExpression}
          nextPage={nextPage}
          previousPage={previousPage}
          setActivePage={setActivePage}
          setAnswers={setAnswers}
          setAnswersAndPublish={setAnswersAndPublish}
          answers={answers}
          scoreExpression={form.scoreExpression ?? undefined}
          setScore={setScore}
          score={score}
          isLoading={isLoading}
        />
      );
    else return null;
  });

  const navbarPages = form?.sortedPages?.map((sortedId: string) => {
    const page = form.pages.find(
      (el: Page) => el.id === sortedId
    );
    if (!page) {
      throw new UnexpectedValueError(page);
    }

    return {
      id: page.id,
      title: page.title,
      type: page.type,
    };
  });

  return (
    <div className={`flex flex-col items-center px-2 sm:px-4 lg:px-8`}>
      <ProgressBar
        pages={navbarPages ?? []}
        setActivePage={setActivePage}
        active={activePage}
        activeIndex={activeIndex}
        isPreview={!!isPreview}
      />
      <Heading level={1} className="text-center page-heading">
        {currentPage?.heading}
      </Heading>
      <Heading level={6} className="text-center italic mb-8 md:mb-16 page-description">
        {currentPage?.description}
      </Heading>
      <Slider>
        <SliderContent>{wrappers}</SliderContent>
      </Slider>
      {/* Custom Theme für das Formular wird hier eingefügt */}
      {form?.theme?.styles ? (
        form.theme.styles.startsWith(":root")
          ? <style global>{`${form.theme.styles}`}</style>
          : <style jsx global>
            {`
            :root {
              ${form.theme.styles}
            }
          `}
          </style>
      ) : null}
    </div>
  );
};

type StaticParams = {
  id: FormShortId;
  handle: string;
};

export async function getServerSideProps(ctx: { params: StaticParams }) {
  const params: StaticParams = ctx.params;
  const form = await getFormData({
    handle: params.handle,
    shortId: params.id,
  });
  try {
    if (!form)
      return { props: { formData: null } };

    return { props: { formData: JSON.stringify(form) } };
  } catch (_err) {
    return { props: { formData: null } };
  }
}

export default FormPage;
